import {Dispatch, SetStateAction, useCallback, useEffect, useState} from 'react';

import {isServer} from 'coreUtils/utils';

import useEventCallback from './useEventCallback';
import useEventListener from './useEventListener';

declare global {
	interface WindowEventMap {
		'local-storage': CustomEvent;
	}
}

type SetValue<T> = Dispatch<SetStateAction<T>>;

/**
 * Custom React hook that provides a way to read and write to local storage.
 * @template T - The type of the value to be stored in local storage.
 * @param {string} key - The key used to identify the value in local storage.
 * @param {T} initialValue - The initial value to be stored in local storage if the key is not present.
 * @returns {[T, SetValue<T>]} - A tuple containing the current value and a function to update the value.
*/
function useLocalStorage<T>(key: string, initialValue: T): [T, SetValue<T>] {
	// Get from local storage then
	// parse stored json or return initialValue
	const readValue = useCallback((): T => {
		// Prevent build error "window is undefined" but keeps working
		if (isServer) {
			return initialValue;
		}

		const item = window.localStorage.getItem(key);

		try {
			return item ? (parseJSON(item) as T) : initialValue;
		} catch (error) {
			console.warn(`Error reading localStorage key “${key}”:`, error);

			return initialValue;
		}
	}, [initialValue, key]);

	// State to store our value
	// Pass initial state function to useState so logic is only executed once
	const [storedValue, setStoredValue] = useState<T>(readValue);

	// Return a wrapped version of useState's setter function that ...
	// ... persists the new value to localStorage.
	const setValue: SetValue<T> = useEventCallback((value) => {
		// Prevent build error "window is undefined" but keeps working
		if (isServer) {
			console.warn(`Tried setting localStorage key “${key}” even though environment is not a client`);
		}

		// Allow value to be a function so we have the same API as useState
		const newValue = value instanceof Function ? value(storedValue) : value;

		try {
			// Save to local storage
			window.localStorage.setItem(key, JSON.stringify(newValue));
		} catch (error) {
			console.warn(`Error setting localStorage key “${key}”:`, error);
		} finally {
			// Save state
			setStoredValue(newValue);

			// We dispatch a custom event so every useLocalStorage hook are notified
			window.dispatchEvent(new Event('local-storage'));
		}
	});

	useEffect(() => {
		setStoredValue(readValue());
	}, []);

	const handleStorageChange = useCallback(
		(event: StorageEvent | CustomEvent) => {
			if ((event as StorageEvent)?.key && (event as StorageEvent).key !== key) {
				return;
			}

			setStoredValue(readValue());
		},
		[key, readValue],
	);

	// this only works for other documents, not the current one
	useEventListener('storage', handleStorageChange);

	// this is a custom event, triggered in writeValueToLocalStorage
	// See: useLocalStorage()
	useEventListener('local-storage', handleStorageChange);

	return [storedValue, setValue];
}

export default useLocalStorage;

/**
 * A wrapper for "JSON.parse()"" to support "undefined" value
 * @todo Should be moved to more generic place.
 * @param {string | null} value - the JSON string to parse.
 * @returns {T | undefined} - the parsed JavaScript object of type T, or undefined if the input string cannot be parsed.
*/
function parseJSON<T>(value: string | null): T | undefined {
	try {
		return value === 'undefined' ? undefined : JSON.parse(value ?? '');
	} catch {
		console.error('🔥 parsing error on', {value});

		return undefined;
	}
}
